import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const normalizePath = (path) => {
  if (!path) return "/";
  return path.endsWith("/") && path.length > 1 ? path.slice(0, -1) : path;
};

const isIgnoredPath = (path, ignorePaths) => {
  const normalized = normalizePath(path);
  return (ignorePaths || []).some((entry) => {
    const normalizedEntry = normalizePath(entry);
    return (
      normalized === normalizedEntry ||
      (normalizedEntry !== "/" && normalized.startsWith(`${normalizedEntry}/`))
    );
  });
};

export default function PersistRoute({
  storageKey,
  rootPath = "/",
  ignorePaths = [],
  restoreOnPaths = [],
  children,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = React.useRef(false);

  const shouldRestoreFrom = React.useCallback(
    (path) => {
      if (!path) return false;
      const normalized = normalizePath(path);
      if (normalizePath(rootPath) === normalized) return true;
      if (restoreOnPaths.length === 0) return false;
      return restoreOnPaths.some(
        (entry) => normalizePath(entry) === normalized,
      );
    },
    [rootPath, restoreOnPaths],
  );

  React.useEffect(() => {
    if (restoredRef.current) return;
    const stored =
      typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
    const current = normalizePath(location.pathname);

    if (
      stored &&
      current !== normalizePath(stored) &&
      shouldRestoreFrom(current) &&
      !isIgnoredPath(stored, ignorePaths)
    ) {
      restoredRef.current = true;
      navigate(stored, { replace: true });
      return;
    }
    restoredRef.current = true;
  }, [location.pathname, navigate, storageKey, rootPath, ignorePaths]);

  React.useEffect(() => {
    const path = location.pathname;
    if (typeof window === "undefined") return;
    if (isIgnoredPath(path, ignorePaths)) return;
    localStorage.setItem(storageKey, path);
  }, [location.pathname, storageKey, ignorePaths]);

  return <>{children}</>;
}
